// If and when this folder filtering is implemented serverside, the frontend filtering can be removed and the query can live here.
import { FoldersOrder } from "@zapier/bff";
import { FolderTreeItem } from "./useQueryFolderTree";
import { FolderTypeQueryState } from "@zapier/utils";
import { findFolderInTree } from "./utils/findFolderInTree";
import { useCurrentUser } from "../user/useCurrentUser";

interface FilterInput {
  folderArray: FolderTreeItem[];
  folderType: FolderTypeQueryState | null | undefined;
  searchQuery: string;
  selectedOwner?: number | null;
}

interface SortInput {
  folders: FolderTreeItem[];
  order?: FoldersOrder;
}

interface FilterAndSortInput {
  folders: FolderTreeItem[];
  order?: FoldersOrder;
  searchQuery: string;
  folderType: FolderTypeQueryState | null | undefined;
  selectedOwner?: number | null;
  disableOrdering?: boolean;
}

const useFoldersFiltering = () => {
  const { user } = useCurrentUser();

  // Include all folders that match or have a child that matches
  // Results show parent folders that have children that match search query and folderType if provided
  const filterFolders = ({
    folderArray,
    folderType,
    searchQuery,
    selectedOwner,
  }: FilterInput): FolderTreeItem[] => {
    const filteredArray = folderArray.reduce<FolderTreeItem[]>(
      (array, folder) => {
        const doesQueryMatchFolder = folder.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        // Show all folders if the folderType is All
        let doesFolderTypeMatchFolder = folderType === FolderTypeQueryState.All;
        // Show shared folders if folderType is Shared
        if (folderType === FolderTypeQueryState.Shared && folder.isShared) {
          doesFolderTypeMatchFolder = true;
          // show personal folders if folderType is Personal
          // and the folder is not shared
          // and the folder owner is the current user OR if the folder owner is the selected owner
        } else if (
          folderType === FolderTypeQueryState.Personal &&
          !folder.isShared &&
          (folder.ownerId === user?.id || folder.ownerId === selectedOwner)
        ) {
          doesFolderTypeMatchFolder = true;
        } else if (
          folderType === FolderTypeQueryState.OwnedByMe &&
          folder.ownerId === user?.id
        ) {
          doesFolderTypeMatchFolder = true;
        } else if (
          folderType === FolderTypeQueryState.SharedWithMe &&
          folder.isShared &&
          folder.ownerId !== user?.id
        ) {
          doesFolderTypeMatchFolder = true;
        }

        const doesFolderMatchSelectedOwner = selectedOwner === folder.ownerId;

        const hasChildren = folder.children?.length;
        const matchingChildren = hasChildren
          ? filterFolders({
              folderArray: folder.children ?? [],
              searchQuery,
              folderType,
              selectedOwner,
            })
          : [];

        if (
          matchingChildren.length > 0 ||
          (doesQueryMatchFolder &&
            (folderType ? doesFolderTypeMatchFolder : true) &&
            (selectedOwner ? doesFolderMatchSelectedOwner : true))
        ) {
          // Reuse the folder when its whole subtree survived the filter; a fresh
          // object per keystroke invalidates every memoised row.
          const existingChildren: FolderTreeItem[] = folder.children ?? [];
          const childrenUnchanged =
            matchingChildren.length === existingChildren.length &&
            matchingChildren.every((child, i) => child === existingChildren[i]);
          array.push(
            childrenUnchanged
              ? folder
              : { ...folder, children: matchingChildren },
          );
        }

        return array;
      },
      [],
    );

    return filteredArray;
  };

  // Pure: filtering can hand back folders straight from the caller's tree, so
  // sorting them in place would reorder the source data. Identity is preserved
  // wherever nothing actually moved, which is what keeps the rows memoised.
  const sortFolders = ({ folders, order }: SortInput): FolderTreeItem[] => {
    const sorted = [...folders].sort((a, b) => {
      if (order === "asset_count") {
        return (a.assetCount ?? 0) - (b.assetCount ?? 0);
      }
      if (order === "-asset_count") {
        return (b.assetCount ?? 0) - (a.assetCount ?? 0);
      }
      return order === "-title"
        ? b.name.localeCompare(a.name)
        : a.name.localeCompare(b.name);
    });

    const next = sorted.map((folder) => {
      if (!folder.children?.length) return folder;
      const children = sortFolders({ folders: folder.children, order });
      return children === folder.children ? folder : { ...folder, children };
    });

    return next.every((folder, index) => folder === folders[index])
      ? folders
      : next;
  };

  const filterAndSortFoldersRecursively = ({
    order,
    searchQuery,
    folderType,
    selectedOwner,
    disableOrdering,
    folders,
  }: FilterAndSortInput) => {
    const filtered = filterFolders({
      searchQuery,
      folderType,
      selectedOwner,
      folderArray: folders,
    });
    if (!disableOrdering) {
      return sortFolders({ folders: filtered, order });
    }
    return filtered;
  };

  // Recurively find the folder's ancestors using the foldersTree and target parentId
  // 1. Find the parent folder and add it to the beginning of the ancestors array
  // 2. If the parent folder has another parent find it until we hit the root folder
  // This can be used instead of calling the server to get the folder's ancestors (useFolderAncestorsHook)
  const findFolderAncestors = (
    foldersTree: FolderTreeItem[],
    parentId: string | null | undefined,
    ancestors: FolderTreeItem[] = [],
  ): FolderTreeItem[] => {
    if (parentId) {
      const parentFolder = findFolderInTree(foldersTree, parentId);
      ancestors.unshift(parentFolder as FolderTreeItem);
      return findFolderAncestors(
        foldersTree,
        parentFolder?.parentId,
        ancestors,
      );
    } else {
      return ancestors;
    }
  };

  return {
    filterFolders: filterAndSortFoldersRecursively,
    findFolderAncestors,
  };
};

export default useFoldersFiltering;
